import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../config/api';

interface Coordinate {
  latitude: number;
  longitude: number;
  timestamp: string;
}

interface FlightTrack {
  _id: string;
  dateStart: string;
  duration: number;
  coordinates?: Coordinate[];
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function trackDistance(coords: Coordinate[]): number {
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    total += haversineKm(
      coords[i - 1].latitude, coords[i - 1].longitude,
      coords[i].latitude, coords[i].longitude
    );
  }
  return total;
}

function trackMaxSpeed(coords: Coordinate[]): number {
  let max = 0;
  for (let i = 1; i < coords.length; i++) {
    const dist = haversineKm(
      coords[i - 1].latitude, coords[i - 1].longitude,
      coords[i].latitude, coords[i].longitude
    );
    const dtHours =
      (new Date(coords[i].timestamp).getTime() - new Date(coords[i - 1].timestamp).getTime()) /
      3_600_000;
    if (dtHours > 0) max = Math.max(max, dist / dtHours);
  }
  return max;
}

const StatsCards: React.FC = () => {
  const [flights, setFlights] = useState<FlightTrack[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiRequest('/flight-tracks?includeCoordinates=true')
      .then((r) => (r.ok ? r.json() : []))
      .then((data: FlightTrack[]) => setFlights(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const now = new Date();
  const currentMonthFlights = flights.filter((f) => {
    const d = new Date(f.dateStart);
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  });

  // Flight time
  const totalSeconds = currentMonthFlights.reduce((sum, f) => sum + (f.duration || 0), 0);
  const totalHours = Math.floor(totalSeconds / 3600);
  const totalMinutes = Math.floor((totalSeconds % 3600) / 60);
  const flightTimeStr = `${totalHours}:${totalMinutes.toString().padStart(2, '0')} h`;

  // Distance
  const totalDistanceKm = currentMonthFlights.reduce((sum, f) => {
    if (!f.coordinates || f.coordinates.length < 2) return sum;
    return sum + trackDistance(f.coordinates);
  }, 0);
  const distanceStr = `${Math.round(totalDistanceKm)} km`;

  // Max speed (current month)
  const maxSpeed = currentMonthFlights.reduce((max, f) => {
    if (!f.coordinates || f.coordinates.length < 2) return max;
    return Math.max(max, trackMaxSpeed(f.coordinates));
  }, 0);
  const speedStr = `${Math.round(maxSpeed)} km/h`;

  const cards = [
    {
      icon: 'timer',
      title: 'Flugzeit',
      value: flightTimeStr,
      skeletonCol: 'col-5',
    },
    {
      icon: 'route',
      title: 'Distanz',
      value: distanceStr,
      skeletonCol: 'col-4',
    },
    {
      icon: 'speed',
      title: 'Schnellste Geschwindigkeit',
      value: speedStr,
      skeletonCol: 'col-5',
    },
  ];

  return (
    <div className="row mb-4">
      {cards.map((card) => (
        <div key={card.title} className="col-md-4 mb-3">
          <div className="card shadow-sm" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center mb-2">
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: '24px', marginRight: '8px', color: '#687088', userSelect: 'none' }}
                >
                  {card.icon}
                </span>
                <small style={{ color: '#687088' }}>{card.title}</small>
              </div>
              {loading ? (
                <h2 className="mb-0 placeholder-glow" style={{ fontSize: '28px' }}>
                  <span
                    className={`placeholder ${card.skeletonCol}`}
                    style={{ borderRadius: '6px' }}
                  ></span>
                </h2>
              ) : (
                <h2 className="mb-0" style={{ fontSize: '28px', fontWeight: 700, color: '#687088' }}>
                  {card.value}
                </h2>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default StatsCards;
